import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Tooltip,
  DialogContentText,
  TablePagination,
  TableSortLabel,
  Skeleton,
  Checkbox,
  ListItemText,
  Autocomplete,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Download, Upload, Tune as TuneIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import Linkify from 'react-linkify';
import { downloadCsvTemplate, PROJECT_TEMPLATE_COLUMNS } from '../utils/csvTemplate';
import { normalizeDate, splitList } from '../components/bulk/helpers';
import ToolbarButton from '../components/ToolbarButton';
import { getProjectDisplayKey } from '../utils/projectDisplay';
import { WaveLoading } from '../components/Loader';
import StatusPill from '../components/StatusPill';

// Loaded on demand: SheetJS is ~150 kB gzipped and only needed for bulk entry.
const BulkGrid = React.lazy(() => import('../components/bulk/BulkGrid'));

const ProjectManagement = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { canPerformActionByRoute, moduleIdMap, loading: permissionsLoading } = usePermissions();
  const projectsModuleId = moduleIdMap['Projects'];
  
  // Team members get a read-only, compact view — Name, Description, Manager, Status only
  const readOnly = user?.role === 'team_member';
  // Permission checks
  const canAdd = user?.role === 'admin' || canPerformActionByRoute('/projects', 'Add');
  const canEdit = user?.role === 'admin' || canPerformActionByRoute('/projects', 'Edit');
  const canDelete = user?.role === 'admin' || canPerformActionByRoute('/projects', 'Delete');
  const canView = user?.role === 'admin' || readOnly || canPerformActionByRoute('/projects', 'View');
  const columnWidths = readOnly
    ? { name: '28%', description: '37%', manager: '20%', status: '15%' }
    : { name: '15%', potentialId: '7%', description: '15%', requester: '9%', timeline: '10%', billing: '6%', estimate: '7%', manager: '10%', teamLeader: '9%', team: '6%', status: '6%' };
  
  console.log('[ProjectManagement] Permission checks:', {
    userRole: user?.role,
    canAdd,
    canEdit,
    canDelete,
    canView
  });
  
  const canModify = canEdit || canDelete;
  
  // Field visibility state
  const [hiddenFields, setHiddenFields] = useState([]);
  
  // Fetch field visibility for Projects module
  useEffect(() => {
    const fetchFieldVisibility = async () => {
      if (!user?.serviceid || !projectsModuleId) {
        setHiddenFields([]);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/service-management/field-visibility/${user.serviceid}/${projectsModuleId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setHiddenFields(data.hiddenFields || []);
        } else {
          setHiddenFields([]);
        }
      } catch (error) {
        console.error('Error fetching field visibility:', error);
        setHiddenFields([]);
      }
    };
    
    fetchFieldVisibility();
  }, [user?.serviceid, projectsModuleId]);
  
  // Add constants for Marcom service IDs
  const MARCOM_SERVICE_IDS = [35]; // Known Marcom service IDs

  // Helper to check if user is from Marcom service or is admin
  const isUserMarcom = () => {
    if (user?.role === 'admin') return true;
    if (user?.serviceid && MARCOM_SERVICE_IDS.includes(Number(user.serviceid))) return true;
    return false;
  };

  // FW AI SW service ID — for special form behavior (team leader optional, "Other Members" field)
  const FW_AI_SW_SERVICE_ID = 30;
  const isFWAISWService = Number(user?.serviceid) === FW_AI_SW_SERVICE_ID;

  // Helper to check if a field is hidden
  const isFieldHidden = (fieldName) => {
    // Potential ID is mandatory for every service, so it can't be hidden by
    // a service's field-visibility settings (Marcom hides it in the DB).
    if (fieldName === 'potentialId') return false;
    // For requester field, always show it for Marcom users and admins, regardless of hiddenFields
    if (fieldName === 'requester') {
      const isMarcom = isUserMarcom();
      if (isMarcom) {
        return false; // Always show for Marcom users and admins
      }
      return true; // Hide for non-Marcom users
    }
    return hiddenFields.includes(fieldName);
  };
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkGridOpen, setBulkGridOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    projectName: '',
    projectId: '',
    startDate: '',
    endDate: '',
    projectManagerName: '',
    serviceId: ''
  });
  const [filterDateErrors, setFilterDateErrors] = useState({ startDate: '', endDate: '' });
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 5, totalPages: 0 });
  const [formData, setFormData] = useState({
    name: '',
    potentialId: '',
    derivedProjectKey: '',
    description: '',
    requester: '',
    startDate: '',
    endDate: '',
    billingMethod: 'hourly',
    isBillable: true,
    estimatedHours: '',
    estimatedTransactions: '',
    managerId: '',
    status: 'planning',
    teamLeaderId: '',
    teamMemberIds: [],
    otherMemberIds: [],
  });
  // Potential ID the project had when the edit dialog opened — once set it can't be cleared.
  const [originalPotentialId, setOriginalPotentialId] = useState('');
  const [touchedFields, setTouchedFields] = useState({});
  const [currentErrorField, setCurrentErrorField] = useState(null);
  const [formDateErrors, setFormDateErrors] = useState({ startDate: '', endDate: '' });
  const [submitting, setSubmitting] = useState(false);

  // Team Leader is required unless the service is FW AI SW, or nobody in the
  // picker's user list holds the Team Leader role (then there is nothing to pick,
  // and blocking the save just reads as "the option is missing").
  const hasTeamLeaders = users.some((u) => u.role === 'team_leader');
  // Until the user list has loaded, keep it required so a quick submit can't slip past the check.
  const teamLeaderRequired = !isFieldHidden('teamLeaderId') && !isFWAISWService && (users.length === 0 || hasTeamLeaders);

  const getFirstMissingRequired = (data) => {
    if (!isFieldHidden('name') && !data.name) return 'name';
    if (!isFieldHidden('description') && !data.description) return 'description';
    if (!isFieldHidden('requester') && isUserMarcom() && !data.requester) return 'requester';
    if (!isFieldHidden('managerId') && !data.managerId) return 'managerId';
    if (teamLeaderRequired && !data.teamLeaderId) return 'teamLeaderId';
    if (!isFieldHidden('teamMemberIds') && (!data.teamMemberIds || !Array.isArray(data.teamMemberIds) || data.teamMemberIds.length === 0)) return 'teamMemberIds';
    return null;
  };

  useEffect(() => {
    if (user) {
      fetchProjects(1, 10000); // Load all projects for client-side filtering
      if (user.role !== 'team_member') {
        fetchUsers(); // This will use the scoped endpoint
      }
      // Fetch services for admin users
      if (user.role === 'admin') {
        fetchServices();
      }
    }
  }, [user]);

  const fetchProjects = async (pageNum = 1, limit = 10000) => {
    try {
      setLoadingProjects(true);
      const url = `${process.env.REACT_APP_API_URL}/projects?page=${pageNum}&limit=${limit}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch projects');
      
      const data = await response.json();
      setProjects(Array.isArray(data) ? data : data.data || []);
      // Ignore server pagination when loading all for client-side filtering
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to fetch projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoadingServices(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch services');
      }
      
      const data = await res.json();
      setServices(data);
    } catch (err) {
      console.error('Fetch services error:', err);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchUsers = async (includeAssignedIds = []) => {
    try {
      // Use project management specific endpoint that bypasses teamScope restrictions
      let url = `${process.env.REACT_APP_API_URL}/users/for-project-management`;
      
      // If we have assigned user IDs to include, add them to the query
      if (includeAssignedIds.length > 0) {
        const assignedIdsParam = includeAssignedIds.join(',');
        url += `?includeAssigned=${assignedIdsParam}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to active users if project management endpoint fails
      try {
        const fallbackUrl = `${process.env.REACT_APP_API_URL}/users/active`;
        const fallbackResponse = await fetch(fallbackUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          setUsers(Array.isArray(fallbackData) ? fallbackData : fallbackData.data || []);
        }
      } catch (fallbackError) {
        console.error('Fallback user fetch failed:', fallbackError);
      }
    }
  };

  const handleOpen = (project = null) => {
    setError('');
    setSuccess('');
    setTouchedFields({}); // Reset touched fields
    setCurrentErrorField(null);
    setFormDateErrors({ startDate: '', endDate: '' });
    if (project) {
      // Determine team leader and member IDs from the association
      const leader = project.teamMembers?.find((tm) => tm.ProjectTeam.role === 'lead');
      const members = project.teamMembers
        ?.filter((tm) => tm.ProjectTeam.role === 'member')
        .map((tm) => tm.id);
      const others = project.teamMembers
        ?.filter((tm) => tm.ProjectTeam.role === 'other')
        .map((tm) => tm.id);

      const managerId = project.managerId || '';
      const teamLeaderId = leader?.id || '';
      const teamMemberIds = members || [];
      const otherMemberIds = others || [];

      setFormData({
        name: project.name,
        potentialId: project.potentialId || '',
        derivedProjectKey: getProjectDisplayKey(project),
        description: project.description,
        requester: project.requester || '',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        billingMethod: project.billingMethod || 'hourly',
        isBillable: project.isBillable !== false,
        estimatedHours: project.estimatedHours || '',
        estimatedTransactions: project.estimatedTransactions || '',
        managerId: managerId,
        status: project.status || 'planning',
        teamLeaderId: teamLeaderId,
        teamMemberIds: teamMemberIds,
        otherMemberIds: otherMemberIds,
      });
      setOriginalPotentialId(project.potentialId || '');
      setEditId(project.id);
      
      // Fetch users including currently assigned ones to preserve existing assignments
      const allAssignedIds = [managerId, teamLeaderId, ...teamMemberIds].filter(id => id);
      fetchUsers(allAssignedIds);
    } else {
      setFormData({
        name: '',
        potentialId: '',
        derivedProjectKey: '',
        description: '',
        requester: '',
        startDate: '',
        endDate: '',
        billingMethod: 'hourly',
        isBillable: true,
        estimatedHours: '',
        estimatedTransactions: '',
        managerId: '',
        status: 'planning',
        teamLeaderId: '',
        teamMemberIds: [],
      });
      setOriginalPotentialId('');
      setEditId(null);
      
      // Fetch users normally for new projects (only active users)
      fetchUsers();
    }
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    // Sequential required validation
    const firstMissing = getFirstMissingRequired(formData);
    if (firstMissing) {
      setTouchedFields({ ...touchedFields, [firstMissing]: true });
      setCurrentErrorField(firstMissing);
      const requiredFields = ['Name', 'description', 'project manager'];
      if (teamLeaderRequired) {
        requiredFields.push('team leader');
      }
      if (!isFieldHidden('teamMemberIds')) {
        requiredFields.push('team members');
      }
      setError(`${requiredFields.join(', ')} are required`);
      return;
    }

    // Additional validation check (respecting field visibility)
    if (!formData.name || !formData.description || !formData.managerId || (teamLeaderRequired && !formData.teamLeaderId) || (!isFieldHidden('requester') && isUserMarcom() && !formData.requester)) {
      const requiredFields = ['Name', 'description', 'project manager'];
      if (teamLeaderRequired) {
        requiredFields.push('team leader');
      }
      if (!isFieldHidden('requester') && isUserMarcom()) {
        requiredFields.push('requester');
      }
      setError(`${requiredFields.join(', ')} are required`);
      return;
    }

    // Potential ID: required for new projects (all services). Older projects
    // without one can still be edited, but an existing one can't be cleared.
    const potentialIdStr = (formData.potentialId || '').trim();
    if (!potentialIdStr) {
      if (!editId) {
        setError('Potential ID is required. For internal or non-billable work, create a dummy potential in SD and use that number.');
        return;
      }
      if (originalPotentialId) {
        setError('Potential ID cannot be removed once set');
        return;
      }
    } else if (!/^\d{7}$/.test(potentialIdStr)) {
      setError('Potential ID must be exactly 7 digits');
      return;
    }

    // Validate team member assignments to prevent conflicts
    const teamLeaderId = formData.teamLeaderId ? parseInt(formData.teamLeaderId, 10) : null;
    const teamMemberIds = Array.isArray(formData.teamMemberIds) 
      ? formData.teamMemberIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
      : [];

    // Check if team leader is also selected as team member
    if (teamLeaderId && teamMemberIds.includes(teamLeaderId)) {
      setError('Cannot assign the same user as both team leader and team member. Please select different users for each role.');
      return;
    }

    // Check for duplicate team members
    const uniqueTeamMemberIds = [...new Set(teamMemberIds)];
    if (uniqueTeamMemberIds.length !== teamMemberIds.length) {
      setError('Duplicate team members detected. Please remove duplicates.');
      return;
    }

    setSubmitting(true);
    try {
      const method = editId ? 'PUT' : 'POST';
      const url = editId
        ? `${process.env.REACT_APP_API_URL}/projects/${editId}`
        : `${process.env.REACT_APP_API_URL}/projects`;
      console.log(`Submitting project ${method} to ${url}`, formData);
      
      // Build payload ensuring nullable DB fields are sent as null when empty
      // Exclude hidden fields from submission
      const payloadBase = { ...formData };
      hiddenFields.forEach(field => {
        delete payloadBase[field];
      });
      
      // Project display key is client-side derived (`${service.Service}-${id}`);
      // it's never sent to the backend.
      delete payloadBase.derivedProjectKey;

      const payload = {
        ...payloadBase,
        // DB-nullable fields → null if left blank
        potentialId: (!isFieldHidden('potentialId') && formData.potentialId && formData.potentialId.trim() !== '') ? formData.potentialId.trim() : null,
        requester: (!isFieldHidden('requester') && formData.requester && formData.requester.trim() !== '') ? formData.requester.trim() : null,
        startDate: (!isFieldHidden('startDate') && formData.startDate) ? formData.startDate : null,
        endDate: (!isFieldHidden('endDate') && formData.endDate) ? formData.endDate : null,
        // Keep defaults if chosen; otherwise allow backend defaults by sending null
        billingMethod: (!isFieldHidden('billingMethod') && formData.billingMethod) ? formData.billingMethod : null,
        isBillable: formData.isBillable !== false,
        status: (!isFieldHidden('status') && formData.status) ? formData.status : null,
        // Numeric optional fields → null if blank (backend has defaults)
        estimatedHours: (!isFieldHidden('estimatedHours') && formData.estimatedHours !== '') ? (parseInt(formData.estimatedHours) || 0) : null,
        estimatedTransactions: (!isFieldHidden('estimatedTransactions') && formData.estimatedTransactions !== '') ? (parseInt(formData.estimatedTransactions) || 0) : null,
        // Required/relationship fields
        managerId: (!isFieldHidden('managerId') && formData.managerId) ? parseInt(formData.managerId, 10) : null,
        teamLeaderId: (!isFieldHidden('teamLeaderId')) ? teamLeaderId : null,
        teamMemberIds: (!isFieldHidden('teamMemberIds')) ? teamMemberIds : [],
        // Only sent for FW AI SW service — array of user IDs for "Other Members"
        otherMemberIds: isFWAISWService && Array.isArray(formData.otherMemberIds)
          ? formData.otherMemberIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
          : [],
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });
      console.log(`Response status: ${response.status} ok: ${response.ok}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${editId ? 'update' : 'create'} project`);
      }
      const data = await response.json();
      console.log('Project response data:', data);
      
      setSuccess(editId ? 'Project updated successfully!' : 'Project created successfully!');
      setTimeout(() => {
        handleClose();
        fetchProjects();
      }, 1500);
    } catch (error) {
      console.error(`Error ${editId ? 'updating' : 'creating'} project:`, error);
      setError(error.message);
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    // Mark field as touched
    const newTouched = { ...touchedFields, [e.target.name]: true };
    setTouchedFields(newTouched);
    // Advance sequential error if the current one is fixed
    const firstMissing = getFirstMissingRequired(updated);
    setCurrentErrorField(firstMissing);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
    setSuccess('');
    setSubmitting(false);
    setEditId(null);
    setTouchedFields({});
    setFormDateErrors({ startDate: '', endDate: '' });
    setFormData({
      name: '',
      potentialId: '',
      derivedProjectKey: '',
      description: '',
      requester: '',
      startDate: '',
      endDate: '',
      billingMethod: 'hourly',
      isBillable: true,
      estimatedHours: '',
      estimatedTransactions: '',
      managerId: '',
      status: 'planning',
      teamLeaderId: '',
      teamMemberIds: [],
      otherMemberIds: [],
    });
    setOriginalPotentialId('');
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete project');
      }

      setSuccess('Project deleted successfully!');
      setDeleteOpen(false);
      setProjectToDelete(null);
      fetchProjects();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error.message);
      setDeleteOpen(false);
      setProjectToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setProjectToDelete(null);
  };

  // ---- Bulk grid (paste / import / type) --------------------------------------
  const bulkColumns = useMemo(() => {
    const emailsOf = (pred) => users.filter((u) => pred(u) && u.isActive !== false).map((u) => u.email).filter(Boolean).sort();
    const cols = [
      { key: 'name', label: 'Project name', type: 'text', required: true, width: 220, aliases: ['project', 'title'] },
      { key: 'description', label: 'Description', type: 'text', required: true, width: 220, aliases: ['details', 'desc'] },
      { key: 'potentialId', label: 'Potential ID', type: 'text', required: true, width: 110, aliases: ['potential', 'sd potential', 'potential number'], help: '7 digits. For internal work use a dummy potential created in SD.' },
      { key: 'managerEmail', label: 'Project manager', type: 'lookup', required: true, width: 230, lookupOptions: emailsOf((u) => ['project_manager', 'admin'].includes(u.role)), emails: true, aliases: ['manager', 'pm', 'project manager email', 'manager email', 'owner', 'account manager'] },
      { key: 'teamLeaderEmail', label: 'Team leader', type: 'lookup', width: 230, lookupOptions: emailsOf((u) => u.role === 'team_leader'), emails: true, aliases: ['team lead', 'lead', 'tl', 'team leader email', 'leader'] },
      { key: 'teamMemberEmails', label: 'Team members', type: 'multilookup', width: 260, lookupOptions: emailsOf((u) => !['admin', 'project_manager', 'team_leader'].includes(u.role)), emails: true, aliases: ['members', 'team', 'resources', 'team member emails', 'staff', 'people'], placeholder: 'emails…' },
      { key: 'status', label: 'Status', type: 'select', width: 130, options: ['planning', 'active', 'on_hold', 'completed', 'cancelled'].map((v) => ({ value: v, label: v.replace('_', ' ') })), aliases: ['state', 'stage'], help: 'Blank means planning.',
        synonyms: { 'on-hold': 'on_hold', onhold: 'on_hold', hold: 'on_hold', paused: 'on_hold', live: 'active', ongoing: 'active', 'in progress': 'active', running: 'active', open: 'active', new: 'planning', proposed: 'planning', pipeline: 'planning', done: 'completed', closed: 'completed', finished: 'completed', complete: 'completed', dropped: 'cancelled', canceled: 'cancelled' } },
      { key: 'billable', label: 'Billable', type: 'select', width: 100, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }], aliases: ['billing', 'chargeable', 'is billable'], help: 'Blank means Yes.',
        synonyms: { y: 'yes', true: 'yes', '1': 'yes', billable: 'yes', chargeable: 'yes', n: 'no', false: 'no', '0': 'no', 'non-billable': 'no', 'non billable': 'no', nonbillable: 'no', internal: 'no', 'not billable': 'no' } },
      { key: 'billingMethod', label: 'Billing', type: 'select', width: 140, options: [{ value: 'hourly', label: 'Hourly' }, { value: 'fixed', label: 'Fixed' }, { value: 'per_transaction', label: 'Per transaction' }], aliases: ['billing method', 'billing type', 'pricing'], help: 'Blank means hourly.',
        synonyms: { 'per hour': 'hourly', hour: 'hourly', 'time and material': 'hourly', 't&m': 'hourly', 'fixed price': 'fixed', 'fixed cost': 'fixed', fp: 'fixed', 'per txn': 'per_transaction', 'per transaction': 'per_transaction', transaction: 'per_transaction', transactional: 'per_transaction', 'per unit': 'per_transaction' } },
      { key: 'startDate', label: 'Start date', type: 'date', width: 140, aliases: ['start', 'from'] },
      { key: 'endDate', label: 'End date', type: 'date', width: 140, aliases: ['end', 'to', 'deadline'] },
      { key: 'estimatedHours', label: 'Est. hours', type: 'number', width: 100, aliases: ['estimated hours', 'hours', 'estimate'] },
      { key: 'estimatedTransactions', label: 'Est. transactions', type: 'number', width: 120, aliases: ['estimated transactions', 'transactions', 'tx'] },
    ];
    if (isUserMarcom()) cols.splice(3, 0, { key: 'requester', label: 'Requester', type: 'text', required: true, width: 200, aliases: ['requested by', 'client contact'] });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, user?.serviceid, user?.role]);

  const validateProjectRow = useCallback((v, ctx) => {
    const errors = {};
    const lc = (x) => String(x || '').trim().toLowerCase();
    const potentialId = String(v.potentialId || '').trim();
    if (potentialId && !/^\d{7}$/.test(potentialId)) errors.potentialId = 'Must be exactly 7 digits';
    const find = (email) => users.find((u) => lc(u.email) === lc(email));
    const manager = find(v.managerEmail);
    if (v.managerEmail && !manager && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.managerEmail.trim())) errors.managerEmail = 'Not an email address';
    const teamLeader = find(v.teamLeaderEmail);
    const memberEmails = splitList(v.teamMemberEmails);
    const teamMemberIds = []; const unresolved = [];
    memberEmails.forEach((e) => { const u = find(e); if (u) teamMemberIds.push(u.id); else unresolved.push(e); });
    const badEmail = memberEmails.find((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (badEmail) errors.teamMemberEmails = `"${badEmail}" is not an email address`;
    if (teamLeader && teamMemberIds.includes(teamLeader.id)) errors.teamMemberEmails = 'The team leader cannot also be a team member';
    const startDate = v.startDate ? normalizeDate(v.startDate, ctx.dayFirst) : null;
    const endDate = v.endDate ? normalizeDate(v.endDate, ctx.dayFirst) : null;
    if (startDate && endDate && endDate < startDate) errors.endDate = 'End date is before the start date';
    const estHours = v.estimatedHours ? parseFloat(v.estimatedHours) : null;
    const estTrans = v.estimatedTransactions ? parseInt(v.estimatedTransactions, 10) : null;
    return {
      errors,
      payload: {
        name: String(v.name || '').trim(),
        description: String(v.description || '').trim(),
        potentialId: potentialId || null,
        requester: v.requester || null,
        startDate, endDate,
        billingMethod: v.billingMethod || 'hourly',
        isBillable: lc(v.billable) !== 'no',
        status: v.status || 'planning',
        estimatedHours: estHours || 0,
        estimatedTransactions: estTrans || 0,
        managerId: manager ? manager.id : null,
        managerEmail: manager ? null : (v.managerEmail || '').trim() || null,
        teamLeaderId: teamLeader ? teamLeader.id : null,
        teamLeaderEmail: teamLeader ? null : (v.teamLeaderEmail || '').trim() || null,
        teamMemberIds,
        teamMemberEmails: unresolved,
      },
    };
  }, [users]);

  const projectDedupeKey = useCallback((v) => (v.name ? v.name.trim().toLowerCase() : null), []);

  const postBulkProjects = async (payloads, dryRun) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ projects: payloads, dryRun }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Bulk upload failed');
    return data;
  };
  const bulkDryRun = (payloads) => postBulkProjects(payloads, true);
  const bulkSubmit = async (payloads) => {
    const data = await postBulkProjects(payloads, false);
    fetchProjects();
    return data;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  // Compact form for the projects table timeline column — "May 7" or "May 7, 26" if in a prior/future year
  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString(undefined, sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: '2-digit' });
  };

  // Admin CSV export — respects current filters/sort by pulling from filteredProjects
  const handleExportProjects = () => {
    const rows = filteredProjects;
    if (!rows.length) {
      setError('No projects match the current filters — nothing to export.');
      return;
    }

    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return '';
      const s = String(value);
      if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const headers = [
      'Name', 'Project Key', 'Potential ID', 'Description', 'Requester',
      'Start Date', 'End Date', 'Billable', 'Billing Method',
      'Estimated Hours', 'Estimated Transactions',
      'Manager', 'Team Leader', 'Team Members',
      'Status',
    ];
    const csvRows = [headers.join(',')];

    rows.forEach((p) => {
      const lead = p.teamMembers?.find(tm => tm.ProjectTeam?.role === 'lead')?.name || '';
      const members = (p.teamMembers?.filter(tm => tm.ProjectTeam?.role === 'member') || [])
        .map(m => m.name)
        .join('; ');
      csvRows.push([
        escapeCsvValue(p.name),
        escapeCsvValue(getProjectDisplayKey(p)),
        escapeCsvValue(p.potentialId),
        escapeCsvValue(p.description),
        escapeCsvValue(p.requester),
        escapeCsvValue(p.startDate ? String(p.startDate).slice(0, 10) : ''),
        escapeCsvValue(p.endDate ? String(p.endDate).slice(0, 10) : ''),
        escapeCsvValue(p.isBillable === false ? 'No' : 'Yes'),
        escapeCsvValue(p.billingMethod),
        escapeCsvValue(p.estimatedHours ?? ''),
        escapeCsvValue(p.estimatedTransactions ?? ''),
        escapeCsvValue(p.manager?.name),
        escapeCsvValue(lead),
        escapeCsvValue(members),
        escapeCsvValue(p.status),
      ].join(','));
    });

    const BOM = '﻿';
    const blob = new Blob([BOM + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `projects_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      projectName: '',
      projectId: '',
      startDate: '',
      endDate: '',
      projectManagerName: '',
      serviceId: ''
    });
    setFilterDateErrors({ startDate: '', endDate: '' });
    setPage(0);
  };
  const handleSort = (prop) => {
    const isAsc = orderBy === prop && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(prop);
  };
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  function descendingComparator(a, b, prop) {
    if (b[prop] < a[prop]) return -1;
    if (b[prop] > a[prop]) return 1;
    return 0;
  }
  function getComparator(ord, prop) {
    return ord === 'desc'
      ? (a, b) => descendingComparator(a, b, prop)
      : (a, b) => -descendingComparator(a, b, prop);
  }
  function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const orderRes = comparator(a[0], b[0]);
      if (orderRes !== 0) return orderRes;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  }

  // Filter projects based on current filters
  const filteredProjects = projects.filter(project => {
    // Project Name filter - supports partial matching for autocomplete
    if (filters.projectName && !project.name.toLowerCase().includes(filters.projectName.toLowerCase())) {
      return false;
    }
    
    // Project ID filter
    if (filters.projectId && !project.potentialId?.toString().includes(filters.projectId)) {
      return false;
    }
    
    // Start Date filter - projects with start date >= filter date
    if (filters.startDate) {
      if (!project.startDate || project.startDate === 'N/A' || project.startDate === null) {
        return false; // Skip projects with null/NA start dates
      }
      const projectStartDate = new Date(project.startDate);
      const filterStartDate = new Date(filters.startDate);
      // Project start date should be >= filter start date
      if (projectStartDate < filterStartDate) {
        return false;
      }
    }
    
    // End Date filter - projects with end date <= filter date
    if (filters.endDate) {
      if (!project.endDate || project.endDate === 'N/A' || project.endDate === null) {
        return false; // Skip projects with null/NA end dates
      }
      const projectEndDate = new Date(project.endDate);
      const filterEndDate = new Date(filters.endDate);
      // Project end date should be <= filter end date
      if (projectEndDate > filterEndDate) {
        return false;
      }
    }
    
    // Project Manager Name filter (only for admin and view_only)
    if (filters.projectManagerName && (user?.role === 'admin' || user?.role === 'view_only')) {
      const managerName = project.manager?.name || '';
      if (!managerName.toLowerCase().includes(filters.projectManagerName.toLowerCase())) {
        return false;
      }
    }
    
    // Service filter (only for admin)
    if (filters.serviceId && user?.role === 'admin') {
      const projectServiceId = project.serviceId ? parseInt(project.serviceId, 10) : null;
      const filterServiceId = parseInt(filters.serviceId, 10);
      if (projectServiceId !== filterServiceId) {
        return false;
      }
    }
    
    return true;
  });

  // Helper function to calculate total column count for table
  const getTotalColumnCount = () => {
    if (readOnly) return 4; // Name, Description, Manager, Status
    let count = 11; // Base columns: Name, Description, Start Date, End Date, Billing Method, Est. Hours, Manager, Team Members, Status
    if (!isFieldHidden('potentialId')) count += 1;
    if (!isFieldHidden('requester')) count += 1;
    if (!isFieldHidden('estimatedTransactions')) count += 1;
    if (!isFieldHidden('teamLeaderId')) count += 1;
    if (canEdit || canDelete) count += 1; // Actions column
    return count;
  };

  // Get unique project names for dropdown
  const uniqueProjectNames = [...new Set(projects.map(p => p.name))].sort();
  
  // Get unique project managers for dropdown (only for admin and view_only)
  const uniqueProjectManagers = (user?.role === 'admin' || user?.role === 'view_only') 
    ? [...new Set(projects.map(p => p.manager?.name).filter(Boolean))].sort()
    : [];

  // Client-side pagination for filtered results
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedFilteredProjects = filteredProjects.slice(startIndex, endIndex);

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>Project Management</Typography>
      
      {/* Filter Section — Notion-style soft bar, no "Filters" title */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
          border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: 2.5,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          {/* Start Date Filter — primary, always visible */}
          <TextField
            size="small"
            label="Start Date From"
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              const newStartDate = e.target.value;
              const newFilterDateErrors = { ...filterDateErrors };

              // Validate start date
              if (newStartDate && filters.endDate && newStartDate > filters.endDate) {
                newFilterDateErrors.startDate = 'Start date cannot be after end date';
              } else {
                newFilterDateErrors.startDate = '';
              }

              // Clear end date error if validation passes
              if (newStartDate && filters.endDate && newStartDate <= filters.endDate) {
                newFilterDateErrors.endDate = '';
              }

              setFilterDateErrors(newFilterDateErrors);
              handleFilterChange('startDate', newStartDate);
            }}
            error={!!filterDateErrors.startDate}
            helperText={filterDateErrors.startDate}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
            inputProps={{
              max: filters.endDate || undefined
            }}
          />

          {/* End Date Filter — primary, always visible */}
          <TextField
            size="small"
            label="End Date To"
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              const newEndDate = e.target.value;
              const newFilterDateErrors = { ...filterDateErrors };

              // Validate end date
              if (newEndDate && filters.startDate && newEndDate < filters.startDate) {
                newFilterDateErrors.endDate = 'End date cannot be before start date';
              } else {
                newFilterDateErrors.endDate = '';
              }

              // Clear start date error if validation passes
              if (newEndDate && filters.startDate && newEndDate >= filters.startDate) {
                newFilterDateErrors.startDate = '';
              }

              setFilterDateErrors(newFilterDateErrors);
              handleFilterChange('endDate', newEndDate);
            }}
            error={!!filterDateErrors.endDate}
            helperText={filterDateErrors.endDate}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
            inputProps={{
              min: filters.startDate || undefined
            }}
          />

          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <ToolbarButton
              variant={showAdvancedFilters ? 'contained' : 'outlined'}
              startIcon={<TuneIcon />}
              onClick={() => setShowAdvancedFilters((v) => !v)}
            >
              More filters
              {(filters.projectName || filters.projectId || filters.projectManagerName || filters.serviceId) && ' •'}
            </ToolbarButton>
            {(filters.projectName || filters.projectId || filters.startDate || filters.endDate || filters.projectManagerName || filters.serviceId) && (
              <ToolbarButton
                variant="outlined"
                onClick={clearFilters}
              >
                Clear All
              </ToolbarButton>
            )}
          </Box>
        </Box>

        {(showAdvancedFilters || filters.projectName || filters.projectId || filters.projectManagerName || filters.serviceId) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              mt: 1.5,
              pt: 1.5,
              borderTop: (t) => t.palette.mode === 'dark' ? '1px dashed rgba(255,255,255,0.08)' : '1px dashed #e5e7eb',
            }}
          >
            {/* Project Name Filter - Autocomplete */}
            <Autocomplete
              size="small"
              options={uniqueProjectNames}
              value={filters.projectName}
              onChange={(event, newValue) => handleFilterChange('projectName', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project Name"
                  placeholder="Type to search projects..."
                />
              )}
              sx={{ minWidth: 200 }}
              freeSolo
              clearOnEscape
              openOnFocus={false}
              disablePortal={false}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return [];
                return options.filter(option =>
                  option.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
            />

            {/* Project ID Filter */}
            {!isFieldHidden('potentialId') && (
              <TextField
                size="small"
                label="Potential ID"
                value={filters.projectId}
                onChange={(e) => handleFilterChange('projectId', e.target.value)}
                placeholder="Enter ID"
                sx={{ minWidth: 200 }}
              />
            )}

            {/* Project Manager Name Filter - Only for Admin and View Only */}
            {(user?.role === 'admin' || user?.role === 'view_only') && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Project Manager</InputLabel>
                <Select
                  value={filters.projectManagerName}
                  onChange={(e) => handleFilterChange('projectManagerName', e.target.value)}
                  label="Project Manager"
                >
                  <MenuItem value="">All Managers</MenuItem>
                  {uniqueProjectManagers.map(name => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Service Filter - Only for Admin */}
            {user?.role === 'admin' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Service</InputLabel>
                <Select
                  value={filters.serviceId}
                  onChange={(e) => handleFilterChange('serviceId', e.target.value)}
                  label="Service"
                  disabled={loadingServices}
                >
                  <MenuItem value="">All Services</MenuItem>
                  {services.map(service => (
                    <MenuItem key={service.Id} value={service.Id.toString()}>
                      {service.Service}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Box>
      {canAdd && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToolbarButton
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Add New Project
          </ToolbarButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            {user?.role === 'admin' && (
              <ToolbarButton
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportProjects}
                sx={{
                  color: 'text.secondary',
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                  '&:hover': {
                    borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                    backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                  },
                }}
              >
                Export Projects
              </ToolbarButton>
            )}
            <ToolbarButton
              variant="outlined"
              startIcon={<Download />}
              onClick={() => downloadCsvTemplate('projects_template.csv', PROJECT_TEMPLATE_COLUMNS)}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Download Template
            </ToolbarButton>
            {(user?.role === 'project_manager' || user?.role === 'admin') && (
              <ToolbarButton
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => setBulkGridOpen(true)}
                sx={{
                  color: 'text.secondary',
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                  '&:hover': {
                    borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                    backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                  },
                }}
              >
                Add Many Projects
              </ToolbarButton>
            )}
          </Box>
        </Box>
      )}
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      {permissionsLoading ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <WaveLoading theme={theme} size="large" />
        </Box>
      ) : !canView ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            You don't have permission to view this data.
          </Typography>
        </Box>
      ) : (
      <TableContainer
        component={Paper}
        sx={{
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.3)' 
              : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.5)' 
                : 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': {
              px: 1.25,
              py: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976d2' }}>
              <TableCell sortDirection={orderBy === 'name' ? order : false} sx={{ fontWeight: 'bold', width: columnWidths.name }}>
                <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')} sx={{ color: 'inherit' }}>Name</TableSortLabel>
              </TableCell>
              {!readOnly && !isFieldHidden('potentialId') && (
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', width: columnWidths.potentialId }}>Potential</TableCell>
              )}
              <TableCell sx={{ fontWeight: 'bold', width: columnWidths.description }}>Description</TableCell>
              {!readOnly && !isFieldHidden('requester') && (
                <TableCell sx={{ fontWeight: 'bold', width: columnWidths.requester }}>Requester</TableCell>
              )}
              {!readOnly && (
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', width: columnWidths.timeline }}>Timeline</TableCell>
              )}
              {!readOnly && (
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', width: columnWidths.billing }}>Billing</TableCell>
              )}
              {!readOnly && (
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', width: columnWidths.estimate }}>Estimate</TableCell>
              )}
              <TableCell sx={{ fontWeight: 'bold', width: columnWidths.manager }}>Manager</TableCell>
              {!readOnly && !isFieldHidden('teamLeaderId') && (
                <TableCell sx={{ fontWeight: 'bold', width: columnWidths.teamLeader }}>Team Leader</TableCell>
              )}
              {!readOnly && (
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', width: columnWidths.team }}>Team</TableCell>
              )}
              <TableCell sx={{ fontWeight: 'bold', width: columnWidths.status }}>Status</TableCell>
              {(canEdit || canDelete) && <TableCell sx={{ fontWeight: 'bold', width: 96 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingProjects ? (
              <TableRow>
                <TableCell
                  colSpan={getTotalColumnCount()}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <WaveLoading theme={theme} size="large" />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      Loading projects...
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center'
                      }}
                    >
                      Please wait while we fetch the latest project data
                    </Typography>
                  </Box>
                </TableCell>
                  </TableRow>
            )
              : stableSort(paginatedFilteredProjects, getComparator(order, orderBy))
                  .map((project) => (
                    <TableRow
                      key={project.id}
                      hover
                      sx={{
                        borderBottom: theme.palette.mode === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.05)'
                          : '1px solid rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                        }
                      }}
                    >
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                        fontWeight: 500,
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
                          <Tooltip title={project.name || ''} placement="top-start">
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {project.name}
                            </Box>
                          </Tooltip>
                          {(() => {
                            const displayKey = getProjectDisplayKey(project);
                            if (!displayKey) return null;
                            return (
                              <Box
                                sx={{
                                  fontSize: '0.75rem',
                                  color: 'text.secondary',
                                  fontWeight: 400,
                                  mt: 0.25,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {displayKey}
                              </Box>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      {!readOnly && !isFieldHidden('potentialId') && (
                        <TableCell sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                          fontFamily: project.potentialId ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
                          fontSize: project.potentialId ? '0.875rem' : undefined,
                        }}>
                          {project.potentialId || '—'}
                        </TableCell>
                      )}
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400,
                      }}>
                        <Tooltip title={project.description || ''} placement="top-start">
                          <Box
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                              <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key} style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                                {decoratedText}
                              </a>
                            )}>
                              {project.description}
                            </Linkify>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {!readOnly && !isFieldHidden('requester') && (
                        <TableCell sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400,
                        }}>
                          <Tooltip title={project.requester || ''} placement="top-start">
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {project.requester || '—'}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      )}
                      {!readOnly && (
                        <TableCell sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                        }}>
                          {project.startDate || project.endDate
                            ? `${project.startDate ? formatDateShort(project.startDate) : '—'} → ${project.endDate ? formatDateShort(project.endDate) : '—'}`
                            : '—'}
                        </TableCell>
                      )}
                      {!readOnly && (
                        <TableCell sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                        }}>
                          {project.billingMethod || '—'}
                          {project.isBillable === false && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Non-billable
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {!readOnly && (
                        <TableCell sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                        }}>
                          {(() => {
                            const hrs = Number(project.estimatedHours) || 0;
                            const tx = Number(project.estimatedTransactions) || 0;
                            const showTx = tx > 0 && !isFieldHidden('estimatedTransactions');
                            if (!hrs && !showTx) return '—';
                            return (
                              <Tooltip
                                title={`${hrs} estimated hour${hrs === 1 ? '' : 's'}${showTx ? ` · ${tx.toLocaleString()} estimated transaction${tx === 1 ? '' : 's'}` : ''}`}
                                placement="top-start"
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                                  <Box>{hrs} hrs</Box>
                                  {showTx && (
                                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                                      {tx.toLocaleString()} tx
                                    </Box>
                                  )}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      )}
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400,
                      }}>
                        <Tooltip title={project.manager?.name || 'Unassigned'} placement="top-start">
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.manager?.name || 'Unassigned'}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {!readOnly && !isFieldHidden('teamLeaderId') && (() => {
                        const leadName = project.teamMembers?.find(tm => tm.ProjectTeam.role === 'lead')?.name || '—';
                        return (
                          <TableCell sx={{
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                            fontWeight: 400,
                          }}>
                            <Tooltip title={leadName} placement="top-start">
                              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {leadName}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })()}
                      {!readOnly && (
                        <TableCell sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                        }}>
                          {(() => {
                            const members = project.teamMembers?.filter(tm => tm.ProjectTeam.role === 'member') || [];
                            if (members.length === 0) return '—';
                            return (
                              <Tooltip title={members.map(m => m.name).join(', ')} placement="top">
                                <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                                  {members.length} {members.length === 1 ? 'member' : 'members'}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      )}
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400
                      }}>
                        <StatusPill status={project.status} />
                      </TableCell>
                      {(canEdit || canDelete) && (
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>
                          {canEdit && (
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={() => handleOpen(project)}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                                  }
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => handleDeleteClick(project)}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                                  }
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
            }
            {!loadingProjects && filteredProjects.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={getTotalColumnCount()}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      No projects found
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center'
                      }}
                    >
                      Try adjusting your search or add a new project
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={Math.max(0, filteredProjects.length)}
          page={Math.max(0, Math.min(page, Math.max(0, Math.ceil(filteredProjects.length / rowsPerPage) - 1)))}
          onPageChange={handleChangePage}
          rowsPerPage={Math.max(1, Math.min(100, rowsPerPage))}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </TableContainer>
      )}

      {/* Add/Edit Project Dialog */}
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // Only close on explicit actions (Cancel button, etc.), not on backdrop click or Escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleClose();
          }
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>{editId ? 'Edit Project' : 'Add New Project'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          {!isFieldHidden('name') && (
            <TextField
              margin="normal"
              name="name"
              label="Project Name *"
              fullWidth
              required
              value={formData.name}
              onChange={handleChange}
              error={currentErrorField === 'name'}
              helperText={currentErrorField === 'name' ? "Project name is required" : ""}
            />
          )}
          {!isFieldHidden('potentialId') && (
            <TextField
              margin="normal"
              name="potentialId"
              label="Potential ID"
              fullWidth
              required={!editId || !!originalPotentialId}
              value={formData.potentialId}
              onChange={handleChange}
              inputProps={{ maxLength: 7, inputMode: 'numeric' }}
              error={!!formData.potentialId && !/^\d{7}$/.test(formData.potentialId.trim())}
              helperText={
                formData.potentialId && !/^\d{7}$/.test(formData.potentialId.trim())
                  ? 'Must be exactly 7 digits'
                  : editId && !originalPotentialId
                    ? 'Not set yet — please add the potential number'
                    : '7 digits. For internal / non-billable work, use a dummy potential created in SD.'
              }
            />
          )}
          {/* Project Key is derived from the project's service + id.
              Read-only on edit (showing the actual identifier); on create it's
              assigned when the record is saved. */}
          <Box
            sx={{
              mt: 2,
              mb: 1,
              px: 1.5,
              py: 1.25,
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
              backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafbfc',
            }}
          >
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
              Project Key
            </Box>
            <Box sx={{ mt: 0.5, fontSize: '0.9375rem', fontWeight: 500, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: editId && formData.derivedProjectKey ? 'text.primary' : 'text.secondary' }}>
              {editId
                ? (formData.derivedProjectKey || `—`)
                : 'Will be assigned on save'}
            </Box>
          </Box>
          {!isFieldHidden('description') && (
            <TextField
              margin="normal"
              name="description"
              label="Description *"
              fullWidth
              required
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
              error={currentErrorField === 'description'}
              helperText={currentErrorField === 'description' ? "Description is required" : ""}
            />
          )}
          {!isFieldHidden('requester') && (
            <TextField
              margin="normal"
              name="requester"
              label="Requester *"
              fullWidth
              required
              value={formData.requester}
              onChange={handleChange}
              error={currentErrorField === 'requester'}
              helperText={currentErrorField === 'requester' ? "Requester is required" : ""}
            />
          )}
          {!isFieldHidden('managerId') && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                options={users.filter(user => user.role === 'project_manager' || user.role === 'admin')}
                getOptionLabel={(option) => option.name}
                value={users.find(user => user.id === parseInt(formData.managerId)) || null}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, managerId: newValue ? newValue.id : '' });
                  setTouchedFields({ ...touchedFields, managerId: true });
                  const firstMissing = getFirstMissingRequired({ ...formData, managerId: newValue ? newValue.id : '' });
                  setCurrentErrorField(firstMissing);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project Manager *"
                    placeholder="Search for manager..."
                    required
                    error={currentErrorField === 'managerId'}
                    helperText={currentErrorField === 'managerId' ? "Project manager is required" : ""}
                  />
                )}
              />
            </FormControl>
          )}
          {!isFieldHidden('teamLeaderId') && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                options={users.filter(user => 
                  user.role === 'team_leader' && 
                  !formData.teamMemberIds.includes(user.id)
                )}
                getOptionLabel={(option) => option.name}
                value={users.find(user => user.id === parseInt(formData.teamLeaderId)) || null}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, teamLeaderId: newValue ? newValue.id : '' });
                  setTouchedFields({ ...touchedFields, teamLeaderId: true });
                  const firstMissing = getFirstMissingRequired({ ...formData, teamLeaderId: newValue ? newValue.id : '' });
                  setCurrentErrorField(firstMissing);
                }}
                noOptionsText={hasTeamLeaders ? 'No match' : 'No one in your service has the Team Leader role yet'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={teamLeaderRequired ? "Team Leader *" : "Team Leader"}
                    placeholder={hasTeamLeaders ? "Search for team leader..." : "No team leaders set up yet"}
                    required={teamLeaderRequired}
                    error={(currentErrorField === 'teamLeaderId' && teamLeaderRequired) || formData.teamMemberIds.includes(parseInt(formData.teamLeaderId, 10))}
                    helperText={
                      currentErrorField === 'teamLeaderId' && teamLeaderRequired
                        ? "Team leader is required"
                        : formData.teamMemberIds.includes(parseInt(formData.teamLeaderId, 10))
                          ? "Cannot select a user who is already selected as team member"
                          : !hasTeamLeaders && users.length > 0
                            ? "Only users with the Team Leader role appear here. None in your service yet: an admin can set the role under Users. You can save without one for now."
                            : ""
                    }
                  />
                )}
              />
            </FormControl>
          )}
          {!isFieldHidden('teamMemberIds') && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={users.filter(user =>
                  (user.role === 'team_member' || user.role === 'partner' || user.role === 'art' || user.role === 'copy' || user.role === 'research' || user.role === 'admin') &&
                  (!formData.teamLeaderId || user.id !== parseInt(formData.teamLeaderId, 10))
                )}
                getOptionLabel={(option) => option.name}
                value={users.filter(user => formData.teamMemberIds.includes(parseInt(user.id)))}
                onChange={(event, newValue) => {
                  const newTeamMemberIds = newValue.map(user => user.id);
                  setFormData({ ...formData, teamMemberIds: newTeamMemberIds });
                  setTouchedFields({ ...touchedFields, teamMemberIds: true });
                  const firstMissing = getFirstMissingRequired({ ...formData, teamMemberIds: newTeamMemberIds });
                  setCurrentErrorField(firstMissing);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Team Members *"
                    placeholder="Search for team members..."
                    required
                    error={
                      currentErrorField === 'teamMemberIds' || 
                      (formData.teamLeaderId && formData.teamMemberIds.includes(parseInt(formData.teamLeaderId, 10)))
                    }
                    helperText={
                      currentErrorField === 'teamMemberIds'
                        ? "At least one team member is required"
                        : formData.teamLeaderId && formData.teamMemberIds.includes(parseInt(formData.teamLeaderId, 10))
                        ? "Cannot select the same user as team leader and team member"
                        : ""
                    }
                  />
                )}
              />
            </FormControl>
          )}
          {/* Other Members — only for FW AI SW service. Lists users with role !== 'team_member' */}
          {isFWAISWService && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={users.filter(u =>
                  u.role !== 'team_member' &&
                  u.id !== parseInt(formData.managerId, 10) &&
                  u.id !== parseInt(formData.teamLeaderId, 10) &&
                  !formData.teamMemberIds.includes(u.id)
                )}
                getOptionLabel={(option) => `${option.name}${option.role ? ` (${option.role.replace('_', ' ')})` : ''}`}
                value={users.filter(u => Array.isArray(formData.otherMemberIds) && formData.otherMemberIds.includes(parseInt(u.id)))}
                onChange={(event, newValue) => {
                  const newOtherMemberIds = newValue.map(u => u.id);
                  setFormData({ ...formData, otherMemberIds: newOtherMemberIds });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Other Members"
                    placeholder="Search for other members..."
                  />
                )}
              />
            </FormControl>
          )}
          {!isFieldHidden('status') && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
              >
                <MenuItem value="planning">Planning</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          )}
          {!isFieldHidden('startDate') && (
            <TextField
              margin="normal"
              name="startDate"
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.startDate}
              onChange={(e) => {
                const newStartDate = e.target.value;
                const updated = { ...formData, startDate: newStartDate };
                const newFormDateErrors = { ...formDateErrors };
                
                // Validate start date
                if (newStartDate && formData.endDate && newStartDate > formData.endDate) {
                  newFormDateErrors.startDate = 'Start date cannot be after end date';
                } else {
                  newFormDateErrors.startDate = '';
                }
                
                // Clear end date error if validation passes
                if (newStartDate && formData.endDate && newStartDate <= formData.endDate) {
                  newFormDateErrors.endDate = '';
                }
                
                setFormData(updated);
                setFormDateErrors(newFormDateErrors);
                const newTouched = { ...touchedFields, startDate: true };
                setTouchedFields(newTouched);
              }}
              error={!!formDateErrors.startDate}
              helperText={formDateErrors.startDate}
              inputProps={{
                max: formData.endDate || undefined
              }}
            />
          )}
          {!isFieldHidden('endDate') && (
            <TextField
              margin="normal"
              name="endDate"
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.endDate}
              onChange={(e) => {
                const newEndDate = e.target.value;
                const updated = { ...formData, endDate: newEndDate };
                const newFormDateErrors = { ...formDateErrors };
                
                // Validate end date
                if (newEndDate && formData.startDate && newEndDate < formData.startDate) {
                  newFormDateErrors.endDate = 'End date cannot be before start date';
                } else {
                  newFormDateErrors.endDate = '';
                }
                
                // Clear start date error if validation passes
                if (newEndDate && formData.startDate && newEndDate >= formData.startDate) {
                  newFormDateErrors.startDate = '';
                }
                
                setFormData(updated);
                setFormDateErrors(newFormDateErrors);
                const newTouched = { ...touchedFields, endDate: true };
                setTouchedFields(newTouched);
              }}
              error={!!formDateErrors.endDate}
              helperText={formDateErrors.endDate}
              inputProps={{
                min: formData.startDate || undefined
              }}
            />
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>Billable</InputLabel>
            <Select
              name="isBillable"
              value={formData.isBillable === false ? 'no' : 'yes'}
              onChange={(e) => setFormData({ ...formData, isBillable: e.target.value === 'yes' })}
              label="Billable"
            >
              <MenuItem value="yes">Billable</MenuItem>
              <MenuItem value="no">Non-billable</MenuItem>
            </Select>
          </FormControl>
          {!isFieldHidden('billingMethod') && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Billing Method</InputLabel>
              <Select
                name="billingMethod"
                value={formData.billingMethod}
                onChange={handleChange}
                label="Billing Method"
              >
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="fixed">Fixed Price</MenuItem>
                <MenuItem value="per_transaction">Per Transaction</MenuItem>
              </Select>
            </FormControl>
          )}
          {!isFieldHidden('estimatedHours') && (
            <TextField
              margin="normal"
              name="estimatedHours"
              label="Estimated Hours"
              type="number"
              fullWidth
              value={formData.estimatedHours}
              onChange={handleChange}
            />
          )}
          {!isFieldHidden('estimatedTransactions') && (
            <TextField
              margin="normal"
              name="estimatedTransactions"
              label="Estimated Transactions"
              type="number"
              fullWidth
              value={formData.estimatedTransactions}
              onChange={handleChange}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} color="inherit" /> : (editId ? 'Save Changes' : 'Add Project')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the project "{projectToDelete?.name}"?
          </DialogContentText>
          {projectToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {projectToDelete.name}<br/>
                <strong>Description:</strong> <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key} style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                    {decoratedText}
                  </a>
                )}>
                  {projectToDelete.description}
                </Linkify><br/>
                <strong>Status:</strong> {projectToDelete.status}<br/>
                <strong>Manager:</strong> {projectToDelete.manager?.name || 'Unassigned'}<br/>
                <strong>Billing Method:</strong> {projectToDelete.billingMethod}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add many projects: type, paste from Excel, or import a file */}
      {bulkGridOpen && (
        <React.Suspense fallback={null}>
        <BulkGrid
          open
          onClose={() => setBulkGridOpen(false)}
          title="Add many projects"
          subtitle="Type, paste a block from Excel or Google Sheets, or import a .xlsx / .csv file. Everything is checked before it is added."
          columns={bulkColumns}
          validateRow={validateProjectRow}
          dedupeKey={projectDedupeKey}
          dryRun={bulkDryRun}
          onSubmit={bulkSubmit}
          submitLabel={(n) => `Add ${n} project${n === 1 ? '' : 's'}`}
          templateName="projects_template.csv"
        />
        </React.Suspense>
      )}
    </Box>
  );
};

export default ProjectManagement;
